import { makeAutoObservable } from "mobx";
import { RArray } from "../../collections";
import { DateRange, DysjointDateRanges } from "../../core";
import { Availability } from "../Availability";
export class OnlinePurchaseOrderFulfillmentTime {
    constructor(params) {
        this.type = "Online";
        this.orderingHours = params.orderingHours;
        this.payload = params.payload;
        this.fulfillmentInstructions = params.fulfillmentInstructions;
        this.now = params.now;
        makeAutoObservable(this);
    }
    get fulfillmentTime() {
        return this.fulfillmentInstructions.fulfillmentTime;
    }
    set fulfillmentTime(value) {
        this.fulfillmentInstructions.fulfillmentTime = value;
    }
    get activeDate() {
        const date = this.fulfillmentTime !== null && this.fulfillmentTime.type === "OnTime"
            ? this.fulfillmentTime.date
            : null;
        if (date === null) {
            return null;
        }
        return this.getDate(date);
    }
    get preorderDateRanges() {
        return this.payload.preorderSettings.dateRanges(this.now);
    }
    /**
     * Returns available days with hours.
     */
    get availableDates() {
        return new RArray(this.preorderDateRanges.ranges).flatMap((preorderDateRange) => preorderDateRange.days.mapOptional((date) => {
            const hours = this.orderingHours
                .fulfillmentTimes({ now: this.now, date })
                .filtered((hour) => preorderDateRange.includes(hour));
            if (hours.isEmpty) {
                return null;
            }
            return { date, hours };
        }));
    }
    getDate(date) {
        return this.availableDates.findWhere((item) => item.date.isSame(date, "day"));
    }
    get isCurrentlyTakingOnlineOrders() {
        return this.orderingHours.isTakingOnlineOrdersAt(this.now);
    }
    get asapOptionAvailable() {
        const endOfDay = this.orderingHours.endOfDay(this.now);
        if (endOfDay === null) {
            return false;
        }
        const todayRanges = DysjointDateRanges.singleton(DateRange.fromDates({
            begin: this.now,
            end: endOfDay,
        }));
        const hasTodayDate = !this.preorderDateRanges.intersection(todayRanges).isEmpty;
        return this.isCurrentlyTakingOnlineOrders && hasTodayDate;
    }
    get inFutureOptionAvailabile() {
        return this.availableDates.some((item) => !item.date.isToday);
    }
    get todayOptionAvailable() {
        return this.availableDates.some((item) => item.date.isToday);
    }
    get availability() {
        const domainDate = this.fulfillmentTime !== null && this.fulfillmentTime.type === "OnTime"
            ? this.fulfillmentTime.date
            : null;
        const availableDate = this.activeDate;
        const isAnyOptionAvailable = this.asapOptionAvailable ||
            this.todayOptionAvailable ||
            this.inFutureOptionAvailabile;
        return Availability.boolean({
            AsapUnavailable: this.fulfillmentTime !== null &&
                this.fulfillmentTime.type === "ASAP" &&
                !this.asapOptionAvailable,
            FulfillmentDateOutOfRange: domainDate !== null && availableDate === null,
            FulfillmentDatesEmpty: this.availableDates.isEmpty && !this.isCurrentlyTakingOnlineOrders,
            FulfillmentTimeOutOfRange: domainDate !== null &&
                availableDate !== null &&
                !availableDate.hours.some((time) => time.fullHour === domainDate.fullHour),
            FulfillmentTimesEmpty: availableDate !== null && availableDate.hours.isEmpty,
            FulfillmentTimeOptionsMissing: !isAnyOptionAvailable,
            PreorderDatesEmpty: this.preorderDateRanges.isEmpty,
        });
    }
}
